import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Phone, Menu, X, ArrowUpRight, ChevronDown,
  Plug, Zap, Building2, Home, Lightbulb, Siren, ShieldCheck,
} from "lucide-react";
import { BUSINESS, SERVICES } from "@/data/content";

// Icon + accent colour lookup for the mega menu, keyed by service id.
const SERVICE_ICONS = {
  "ev-charger-installation": Plug,
  "panel-upgrades": Zap,
  "commercial-wiring": Building2,
  "general-electrical": Home,
  lighting: Lightbulb,
  security: ShieldCheck,
  emergency: Siren,
};

const NAV = [
  { label: "Services", key: "services", hasMega: true },
  { label: "Projects", to: "/projects" },
  { label: "Reviews", to: "/reviews" },
  { label: "Contact", to: "/#contact", anchor: "#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMegaOpen(false);
    setMobileServicesOpen(false);
  }, [pathname]);

  const handleNav = (item) => (e) => {
    if (item.anchor && pathname === "/") {
      e.preventDefault();
      const el = document.querySelector(item.anchor);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const showPreviewBanner = process.env.REACT_APP_ALLOW_INDEXING !== "true";

  return (
    <header
      data-testid="site-header"
      className={`fixed ${showPreviewBanner ? "top-8" : "top-0"} inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-[#262626]"
          : "bg-[#0A0A0A]/40 backdrop-blur-md border-b border-transparent"
      }`}
      onMouseLeave={() => setMegaOpen(false)}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-28 md:h-32">
          <Link
            to="/"
            data-testid="brand-logo"
            className="flex items-center gap-2 group"
          >
            <img
              src="/electromax-logo-dark.png"
              alt="ElectroMax Systems"
              className="h-20 md:h-24 w-auto object-contain"
              draggable="false"
            />
            <span className="sr-only">ElectroMax Systems</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) =>
              n.hasMega ? (
                <button
                  key={n.key}
                  type="button"
                  onMouseEnter={() => setMegaOpen(true)}
                  onFocus={() => setMegaOpen(true)}
                  onClick={() => setMegaOpen((v) => !v)}
                  data-testid="nav-link-services"
                  aria-expanded={megaOpen}
                  aria-haspopup="true"
                  className="inline-flex items-center gap-1 text-sm tracking-wide text-white/70 hover:text-white transition-colors relative py-1"
                >
                  {n.label}
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform duration-300 ${
                      megaOpen ? "rotate-180" : ""
                    }`}
                    strokeWidth={2.5}
                  />
                </button>
              ) : (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={handleNav(n)}
                  onMouseEnter={() => setMegaOpen(false)}
                  data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-sm tracking-wide text-white/70 hover:text-white transition-colors relative py-1"
                >
                  {n.label}
                </Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (pathname === "/") {
                  document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
                } else {
                  navigate("/#contact");
                }
              }}
              data-testid="header-quote-cta"
              className="hidden md:inline-flex items-center gap-1.5 bg-transparent text-white border border-[#262626] hover:border-white hover:bg-white hover:text-black transition-colors text-sm font-bold tracking-wide px-4 py-2.5"
            >
              Request a Quote
              <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.5} />
            </button>
            <a
              href={BUSINESS.phoneHref}
              data-testid="header-call-cta"
              className="hidden sm:inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] text-white text-sm font-bold tracking-wide px-4 py-2.5 transition-colors"
            >
              <span className="relative flex w-2 h-2">
                <span className="absolute inline-flex w-full h-full bg-white em-pulse-dot rounded-full" />
                <span className="relative inline-flex w-2 h-2 bg-white rounded-full" />
              </span>
              <Phone className="w-3.5 h-3.5" strokeWidth={2.5} />
              24/7 · {BUSINESS.phone}
            </a>
            <button
              type="button"
              aria-label="Toggle menu"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              className="md:hidden w-10 h-10 inline-flex items-center justify-center border border-[#262626]"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* MEGA MENU — desktop */}
        <AnimatePresence>
          {megaOpen && (
            <motion.div
              key="mega"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="hidden md:block absolute left-0 right-0 top-full bg-[#0A0A0A]/95 backdrop-blur-2xl border-b border-[#262626]"
              onMouseEnter={() => setMegaOpen(true)}
              data-testid="services-mega-menu"
            >
              <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 py-10">
                <div className="grid md:grid-cols-12 gap-8">
                  <div className="md:col-span-3">
                    <div className="text-[10px] tracking-[0.24em] uppercase text-[#E60000] font-bold mb-4 inline-flex items-center gap-2">
                      <span className="inline-block h-px w-6 bg-[#E60000]" />
                      Services
                    </div>
                    <h3 className="font-display text-3xl lg:text-4xl tracking-tighter font-black leading-[0.95]">
                      Everything <br />
                      <span className="text-white/40">we wire.</span>
                    </h3>
                    <p className="mt-4 text-sm text-white/60 leading-relaxed">
                      One licensed team, six specialties, twelve years across Metro Vancouver.
                    </p>
                    <Link
                      to="/#services"
                      onClick={(e) => {
                        setMegaOpen(false);
                        if (pathname === "/") {
                          e.preventDefault();
                          document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" });
                        }
                      }}
                      className="mt-5 inline-flex items-center gap-1.5 text-[#E60000] font-bold text-xs tracking-wide"
                    >
                      Overview <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.5} />
                    </Link>
                  </div>

                  <div className="md:col-span-9 grid grid-cols-2 lg:grid-cols-3 gap-2">
                    {SERVICES.map((s) => {
                      const Icon = SERVICE_ICONS[s.id] || Zap;
                      return (
                        <Link
                          key={s.id}
                          to={`/services/${s.id === "emergency" ? "emergency" : s.id}`}
                          onClick={() => setMegaOpen(false)}
                          data-testid={`mega-service-${s.id}`}
                          className="group relative bg-[#0F0F0F] border border-[#262626] hover:border-[#E60000] transition-colors p-4 flex gap-3 items-start"
                        >
                          <div className="w-10 h-10 shrink-0 border border-[#262626] group-hover:border-[#E60000] group-hover:bg-[#E60000]/10 transition-colors flex items-center justify-center">
                            <Icon className="w-5 h-5 text-[#E60000]" strokeWidth={2} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <div className="font-display text-sm md:text-base font-bold tracking-tight leading-tight text-white">
                                {s.title}
                              </div>
                              <ArrowUpRight
                                className="w-3.5 h-3.5 text-white/30 group-hover:text-[#E60000] transition-colors shrink-0"
                                strokeWidth={2.5}
                              />
                            </div>
                            <p className="mt-1.5 text-[11px] leading-relaxed text-white/55 line-clamp-2">
                              {s.blurb}
                            </p>
                            {s.tag && (
                              <div className="mt-2 text-[9px] tracking-[0.2em] uppercase text-[#E60000] font-bold">
                                {s.tag}
                              </div>
                            )}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>

                {/* Bottom rail */}
                <div className="mt-8 pt-6 border-t border-[#262626] flex flex-wrap items-center justify-between gap-4">
                  <div className="text-[11px] text-white/50">
                    Not sure which service fits?{" "}
                    <a href={BUSINESS.phoneHref} className="text-white hover:text-[#E60000] font-bold">
                      Call {BUSINESS.phone}
                    </a>{" "}
                    — we&rsquo;ll tell you straight.
                  </div>
                  <Link
                    to="/#contact"
                    onClick={(e) => {
                      setMegaOpen(false);
                      if (pathname === "/") {
                        e.preventDefault();
                        document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
                      }
                    }}
                    className="inline-flex items-center gap-1.5 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold text-xs px-4 py-2.5 transition-colors"
                  >
                    Free Estimate <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.5} />
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile menu */}
        {open && (
          <div
            data-testid="mobile-menu"
            className="md:hidden pb-6 border-t border-[#262626] pt-4 flex flex-col gap-1 bg-[#0A0A0A]"
          >
            {/* Services accordion */}
            <button
              type="button"
              onClick={() => setMobileServicesOpen((v) => !v)}
              className="flex items-center justify-between text-base text-white/85 py-2"
              data-testid="mobile-services-toggle"
            >
              Services
              <ChevronDown
                className={`w-4 h-4 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`}
                strokeWidth={2.5}
              />
            </button>
            {mobileServicesOpen && (
              <div className="pl-2 border-l border-[#262626] ml-1 mb-2 space-y-1">
                {SERVICES.map((s) => {
                  const Icon = SERVICE_ICONS[s.id] || Zap;
                  return (
                    <Link
                      key={s.id}
                      to={`/services/${s.id}`}
                      onClick={() => setOpen(false)}
                      className="flex items-start gap-3 py-2 text-sm text-white/75 hover:text-white"
                    >
                      <Icon className="w-4 h-4 text-[#E60000] mt-0.5 shrink-0" strokeWidth={2} />
                      <span className="min-w-0">
                        <span className="block font-semibold text-white/90">{s.title}</span>
                        <span className="block text-[11px] text-white/50 leading-snug mt-0.5 line-clamp-2">
                          {s.blurb}
                        </span>
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
            {NAV.filter((n) => !n.hasMega).map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={(e) => {
                  handleNav(n)(e);
                  setOpen(false);
                }}
                className="text-base text-white/80 hover:text-white py-2"
              >
                {n.label}
              </Link>
            ))}
            <a
              href={BUSINESS.phoneHref}
              className="mt-3 inline-flex items-center gap-2 bg-[#E60000] text-white text-sm font-bold px-4 py-3 w-fit"
            >
              <Phone className="w-4 h-4" /> {BUSINESS.phone}
            </a>
            <Link
              to="/#contact"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-2 border border-[#262626] text-white text-sm font-bold px-4 py-3 w-fit"
            >
              Request a Quote <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}
